import { forwardRef, type ButtonHTMLAttributes } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md';

// `focus:ring-*` (not `focus-visible:`) keeps the ring always visible on focus —
// the design system asks for an unmissable focus target on every control.
const base =
  'cursor-pointer inline-flex items-center justify-center gap-x-2 font-medium rounded-lg transition-colors focus:outline-hidden focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none';

// Tap targets stay >= 40px tall on both sizes (py + line-height).
const sizes: Record<Size, string> = {
  sm: 'text-sm py-2 px-3 min-h-[40px]',
  md: 'text-sm py-2.5 px-4 min-h-[44px]',
};

const variants: Record<Variant, string> = {
  primary:
    'bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)] focus:ring-[var(--color-accent)]',
  // Text on the amber fill is --color-text, not white: white on #d9861f is 2.85:1
  // and fails even the large-text bar (see docs/features/design-system.md).
  secondary:
    'bg-[var(--color-secondary)] text-[var(--color-text)] hover:bg-[var(--color-secondary-hover)] focus:ring-[var(--color-secondary)]',
  ghost:
    'border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)] hover:border-[var(--color-border-strong)] focus:ring-[var(--color-accent)]',
  danger: 'bg-[var(--color-danger)] text-white hover:opacity-90 focus:ring-[var(--color-danger)]',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
}

/**
 * Brand button. Defaults to the primary (midnight) variant.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', fullWidth = false, className = '', type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={[base, sizes[size], variants[variant], fullWidth ? 'w-full' : '', className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    />
  );
});
